import { useAxios } from "../useAxios";
import { useBudgetList } from "./useBudgetList";
import { useBudgetMutations } from "./useBudgetMutations";
import { useMemo } from "react";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import {
  FEATURE_STATUS_CANCELLED,
  FEATURE_STATUS_COMPLETED,
  FEATURE_STATUS_FAILED,
  FEATURE_STATUS_PENDING,
  FEATURE_STATUS_RERUNNING,
} from "config";
import { useProjectsApi } from "../useProjects";
import type { ProjectState } from "../useProjects/useProjects.types";

type ProjectFeatureStatus = NonNullable<ProjectState["featureStatuses"]>[number];

/**
 * Budget feature statuses for which the budget list is fetched. A cancelled rerun ends like a
 * failed one: the budget rows from earlier runs stay on screen.
 */
const BUDGET_LIST_STATUSES: ReadonlyArray<string> = [
  FEATURE_STATUS_COMPLETED,
  FEATURE_STATUS_FAILED,
  FEATURE_STATUS_CANCELLED,
  FEATURE_STATUS_PENDING,
  FEATURE_STATUS_RERUNNING,
];

export const shouldLoadBudgetList = (status: string | undefined): boolean =>
  status !== undefined && BUDGET_LIST_STATUSES.includes(status);

export interface UseBudgetApiResult {
  // Query results
  budgetData: ReturnType<typeof useBudgetList>["budgetData"];
  budgetId: ReturnType<typeof useBudgetList>["budgetId"];
  isBudgetLoading: boolean;
  isBudgetError: boolean;
  budgetError: Error | null;
  isBudgetFetching: boolean;
  refetchBudget: () => Promise<any>;

  // Mutations
  updateBudgetInfo: ReturnType<typeof useBudgetMutations>["updateBudgetInfo"];
  updateBudget: ReturnType<typeof useBudgetMutations>["updateBudget"];
  updateBudgetCurrency: ReturnType<typeof useBudgetMutations>["updateBudgetCurrency"];
  reRunBudgeting: ReturnType<typeof useBudgetMutations>["reRunBudgeting"];
}

export const useBudgetApi = (enabled: boolean = false): UseBudgetApiResult => {
  const axios = useAxios({ allResponse: false });
  const { projectId, documentId, hasValidIds, project } = useProjectIdentifiers();
  const { useProject } = useProjectsApi({});
  const { data: fetchedProject } = useProject(
    enabled && hasValidIds && !project?.featureStatuses ? projectId : undefined
  );

  const currentProject = project || fetchedProject;

  const budgetFeatureStatus = useMemo(() => {
    if (!enabled || !hasValidIds || !currentProject?.featureStatuses) return undefined;
    return currentProject.featureStatuses.find(
      (featureStatus: ProjectFeatureStatus) => featureStatus.feature === "budget"
    )?.status;
  }, [enabled, hasValidIds, currentProject?.featureStatuses]);

  const budgetStatus = shouldLoadBudgetList(budgetFeatureStatus);

  const isBudgetRunInFlight =
    budgetFeatureStatus === FEATURE_STATUS_PENDING ||
    budgetFeatureStatus === FEATURE_STATUS_RERUNNING;

  const listResult = useBudgetList({
    projectId: projectId || 0,
    documentId: documentId || 0,
    axios,
    hasValidIds,
    budgetStatus,
    isBudgetRunInFlight,
    enabled,
  });

  const mutations = useBudgetMutations({
    projectId: projectId || 0,
    documentId: documentId || 0,
    budgetId: listResult.budgetId,
  });

  return useMemo(
    () => ({
      ...listResult,
      ...mutations,
    }),
    [listResult, mutations]
  );
};

export { useBudgetMutations } from "./useBudgetMutations";
export { useBudgetTemplates } from "./useBudgetTemplates";
export { useBudgetVersions } from "./useBudgetVersions";
export { useBudgetScenarioComparison } from "./useBudgetScenarioComparison";
export { useBudgetTaxIncentiveRecommendations } from "./useBudgetTaxIncentiveRecommendations";
export { useTaxIncentiveCatalog } from "./useTaxIncentiveCatalog";
export { useBudgetBenchmarks } from "./useBudgetBenchmarks";
export { useBudgetBenchmarkMeta } from "./useBudgetBenchmarkMeta";
export { useBudgetVersionDiff } from "./useBudgetVersionDiff";
export { useBudgetVersionDiffCategory } from "./useBudgetVersionDiffCategory";
export {
  MAX_FRINGE_ACCOUNT_NUMBER_LENGTH,
  MAX_FRINGE_ASSIGNMENTS,
  MAX_FRINGE_NAME_LENGTH,
  useCreateBudgetFringe,
} from "./useCreateBudgetFringe";
export {
  useBudgetFringes,
  useDeleteBudgetFringe,
  useUpdateBudgetFringe,
  useUpdateBudgetItemFringes,
} from "./useBudgetFringes";
export type { UpdateBudgetFringePatch } from "./useBudgetFringes";
export type { BudgetTemplate } from "./useBudgetTemplates";
export type { ApplyBudgetTemplateParams } from "./useBudgetMutations";
